<template>
  <div>
    <members-export-dialog v-if="showExportMembersDialog" />
    <members-export-run-dialog
      v-if="showExportMembersRunDialog"
      :task="export_result"
    />
    <members-export-add-attributes-dialog
      v-if="showExportMembersAttributesDialog"
    />
    <rest-list :payload="fetchEndpoint">
      <template>
        <rest-list-column
          prop="title"
          :title="$t('Audience_SharedSegment_ListField_Title')"
          :filterable="true"
          :route="sharedSegmentOverviewRoute"
        />

        <rest-list-column
          prop="project_title"
          :title="$t('Audience_SharedSegment_ListField_Project')"
        />

        <rest-list-column-actions v-if="$can('UserPersonalData::Export')">
          <template slot-scope="scope">
            <rest-list-action
              :click="actionExport"
              :row="scope.row"
              :title="$t('Audiences_Segment_Action_Export_Members')"
              icon="el-icon-download"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListAction from '@/common/components/rest/RestListAction'
import MembersExportRunDialog from '@/modules/audiences/components/MembersExportRunDialog'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog'
import MembersExportDialog from '@/modules/audiences/components/MembersExportDialog'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import { taskStatusEnum } from '@/modules/tasks/enum/TaskRecordEnums'

export default {
  components: {
    MembersExportDialog,
    MembersExportAddAttributesDialog,
    MembersExportRunDialog,
    RestListAction,
    RestList,
    RestListColumn,
    RestListColumnActions
  },

  mounted() {
    EventBus.$on('export-members-show', this.exportMembersShow)
    EventBus.$on('export-members-hide', this.exportMembersHide)
    EventBus.$on('export-members-run-show', this.exportMembersRunShow)
    EventBus.$on('export-members-run-hide', this.exportMembersRunHide)
    EventBus.$on('export-members-cancel', this.exportMembersCancel)
    EventBus.$on(
      'export-members-add-attributes-show',
      this.exportMembersAddAttributesShow
    )
    EventBus.$on(
      'export-members-add-attributes-hide',
      this.exportMembersAddAttributesHide
    )
  },

  destroyed() {
    EventBus.$off('export-members-show')
    EventBus.$off('export-members-hide')
    EventBus.$off('export-members-run-show')
    EventBus.$off('export-members-run-hide')
    EventBus.$off('export-members-add-attributes-show')
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-cancel')
  },

  data() {
    return {
      taskId: null,
      showExportMembersDialog: false,
      showExportMembersRunDialog: false,
      showExportMembersAttributesDialog: false,
      exportParams: {},
      sharedSegmentIdToExport: '',
      export_result: {}
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `shared-segments`
      })
    },

    exportEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/export-shared`
      })
    },

    taskEndpoint() {
      return projectEndpoint({
        url: `task-histories/${this.taskId}?expand=task`
      })
    },

    readonly() {
      return !this.$can('updateProjectItem')
    }
  },

  methods: {
    actionExport(sharedSegment) {
      this.sharedSegmentToExport = sharedSegment
      this.showExportMembersDialog = true
    },

    exportMembersShow() {
      this.showExportMembersDialog = true
    },

    exportMembersHide() {
      this.showExportMembersDialog = false
    },

    exportMembersCancel() {
      this.exportMembersHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    exportMembersRunShow(params) {
      Object.assign(this.exportParams, params)
      this.showExportMembersDialog = false
      this.showExportMembersRunDialog = true
      this.exportMembers()
    },

    exportMembersRunHide() {
      this.showExportMembersRunDialog = false
      this.exportParams = {}
      this.export_result = {}
      clearInterval(this.taskLoop)
      this.taskId = null
    },

    exportMembersAddAttributesShow(params) {
      this.exportParams = params
      this.showExportMembersAttributesDialog = true
    },

    exportMembersAddAttributesHide() {
      this.showExportMembersAttributesDialog = false
    },

    exportMembers() {
      let evaluateData = {
        id: this.sharedSegmentToExport.id,
        ...this.exportParams
      }
      RestApi.post(this.exportEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchExportTask()
          this.taskLoop = setInterval(() => {
            this.fetchExportTask()
          }, 2000)
        })
        .catch(error => {
          this.showExportMembersDialog = false
          this.showExportMembersAttributesDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchExportTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.export_result = data
      })
    },

    handleTaskStatus(task, success, error) {
      switch (task.status) {
        case taskStatusEnum.DONE:
          clearInterval(this.taskLoop)
          success()
          break

        case taskStatusEnum.ERROR:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Error'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break

        case taskStatusEnum.FATAL:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Fatal'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break
      }
    },

    sharedSegmentOverviewRoute(row) {
      return projectRoute({
        name: 'AudienceSharedSegment',
        query: {
          id: row.id
        }
      })
    }
  },

  watch: {
    export_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {},
            () => {
              this.exportMembersHide()
              this.exportMembersRunHide()
            }
          )
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/audiences/pages/SharedSegmentList.vue